import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useHospitalAuth } from '../../contexts/HospitalAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { Stethoscope, User, Lock, Eye, EyeOff, Building2 } from 'lucide-react';

const HospitalLoginPage = () => {
  const { hospitalSlug } = useParams();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hospitalName, setHospitalName] = useState('Hospital');
  const [hospitalUsers, setHospitalUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const { hospitalSignIn } = useHospitalAuth();
  const navigate = useNavigate();

  // Fetch hospital name and users
  useEffect(() => {
    const fetchHospitalData = async () => {
      if (!hospitalSlug) {
        // If no slug in URL, try to get from localStorage or use default
        const storedHospital = localStorage.getItem('hospital_data');
        if (storedHospital) {
          try {
            const hospital = JSON.parse(storedHospital);
            setHospitalName(hospital.name || 'Hospital');
          } catch (e) {
            console.error('Error parsing hospital data:', e);
          }
        }
        setLoadingUsers(false);
        return;
      }

      try {
        setLoadingUsers(true);
        const data = await apiRequest(`get_hospital_users_by_slug.php?slug=${hospitalSlug}`);
        
        if (data.success) {
          setHospitalName(data.hospital.name || 'Hospital');
          setHospitalUsers(data.users || []);
        } else {
          toast({
            title: 'Error',
            description: data.error || 'Failed to load hospital data',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Failed to load hospital data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load hospital information',
          variant: 'destructive'
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchHospitalData();
  }, [hospitalSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please select username and enter password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Use username (which is actually email) for login
      const { error } = await hospitalSignIn(formData.username, formData.password);
      
      if (!error) {
        toast({
          title: 'Welcome!',
          description: 'Successfully logged in.',
        });
        
        // Small delay to ensure localStorage is set and context updates
        setTimeout(() => {
          // Get the hospital and user data to determine redirect based on role
          const storedUser = localStorage.getItem('hospital_user');
          const storedHospital = localStorage.getItem('hospital_data');
          
          if (storedUser && storedHospital) {
            try {
              const user = JSON.parse(storedUser);
              const hospital = JSON.parse(storedHospital);
              const hospitalSlug = hospital.slug || hospital.hospital_slug;
              
              if (!hospitalSlug) {
                console.error('Hospital slug not found, using fallback route');
                navigate('/admin/dashboard');
                return;
              }
              
              // Redirect based on user role
              // CLINIC_ADMIN goes to admin dashboard
              // CHIEF_DOCTOR goes to doctor dashboard by default (can toggle "View as Admin" later)
              if (user.role === 'CLINIC_ADMIN') {
                // Clinic admin goes to admin dashboard
                navigate(`/${hospitalSlug}/admin/dashboard`, { replace: true });
              } else {
                // CHIEF_DOCTOR and other roles go to doctor dashboard
                navigate(`/${hospitalSlug}/doctor/dashboard`, { replace: true });
              }
            } catch (parseError) {
              console.error('Error parsing hospital data:', parseError);
              navigate('/admin/dashboard');
            }
          } else {
            // Fallback if user data not available
            console.error('Hospital user or data not found in localStorage');
            navigate('/admin/dashboard');
          }
        }, 100); // Small delay to ensure state updates
      } else {
        toast({
          title: 'Login Failed',
          description: error.message || 'Invalid email or password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{hospitalName} Login - Dental Hub</title>
        <meta name="description" content={`Login to your ${hospitalName} account to manage your dental practice.`} />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                <span className="text-xl sm:text-2xl font-bold gradient-text">{hospitalName}</span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Welcome Back</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Sign in to your Hospital EHR 
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : !hospitalSlug && hospitalUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-300 mb-4">
                    Please access this page through your hospital's login URL.
                  </p>
                  <p className="text-gray-400 text-sm">
                    Format: /your-hospital-slug/login
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-white">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                      <Select
                        value={formData.username}
                        onValueChange={(value) => setFormData({ ...formData, username: value })}
                        required
                      >
                        <SelectTrigger className="pl-10 bg-gray-800 border-gray-600 text-white h-10">
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {hospitalUsers.length > 0 ? (
                            hospitalUsers.map((user) => (
                              <SelectItem 
                                key={user.id} 
                                value={user.email}
                                className="text-white focus:bg-gray-700"
                              >
                                {user.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="" disabled className="text-gray-400">
                              No users available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                  <Button 
                    type="submit" 
                    className="w-full medical-gradient text-white hover:opacity-90"
                    disabled={loading}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>
              )}

              <div className="mt-6 text-center">
                {hospitalSlug ? (
                  <Link 
                    to={`/${hospitalSlug}`}
                    className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm"
                  >
                    ← Back to Home
                  </Link>
                ) : (
                  <Link 
                    to="/"
                    className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm"
                  >
                    ← Back to Home
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default HospitalLoginPage;

